import React from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { MaybeTooltip } from '@modernloop/shared/components';
import { CanDisable, PublicError } from '@modernloop/shared/helper-components';
import { CrossIcon, TriangleDownIcon } from '@modernloop/shared/icons';
import {
  AutocompleteRenderGroupParams,
  Button,
  CircularProgress,
  IconButton,
  LinearProgress,
  ListItem,
  ListItemButton,
  ListSubheader,
  Stack,
  Typography,
} from '@mui/material';
import { first } from 'lodash';

import FilterSelect from 'src/components/FilterSelect';

import { Categories, ZoomUserDataWithCategory, ZoomUserSelectProps } from './types';
import useZoomUserSelectData from './useZoomUserSelectData';

const EMPTY_ROW_ID = 'loading-empty-row';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const GQLZoomUserFragment = gql`
  fragment ZoomUserFragment on ZoomUser {
    orgId
    zoomUserId
    status
    email
    employeeId
    firstName
    lastName
  }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const GQLZoomUsersListQuery = gql`
  query ZoomUsersList($input: ZoomUsersInput!) {
    zoomUsers(input: $input) {
      items {
        ...ZoomUserFragment
      }
      nextCursor
    }
  }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const GQLZoomUsersByIdsQuery = gql`
  query ZoomUsersByIds($input: ZoomUsersByIdsInput!) {
    zoomUsersByIds(input: $input) {
      ...ZoomUserFragment
    }
  }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const GQLZoomUserByIdsQuery = gql`
  query ZoomUserByIds($input: ZoomUsersByIdsInput!) {
    zoomUsersByIds(input: $input) {
      ...ZoomUserFragment
    }
  }
`;

const ZoomUserSelect = <TData extends ZoomUserDataWithCategory, InputValue extends string | string[] | undefined>(
  props: ZoomUserSelectProps<TData, InputValue>
): JSX.Element => {
  const {
    dataTestId,
    disabled,
    getLabel,
    isAdditionalOptionId,
    renderAdditionalOption,
    renderAdditionalOptionGroup,
    getAdditionalOptionSize,
    getAdditionalOptionLabel,
  } = props;

  const { t } = useTranslation();
  const {
    filterListSelectInterfaceRef,
    handleChange,
    handleRemoveRecentAccount,
    handleScrollToEnd,
    isFetchingMore,
    onInputChange,
    options,
    selectedUsers,
    selectedValues,
    isZoomUsersListLoading,
    isZoomUsersListQueryError,
    isSearchEmpty,
  } = useZoomUserSelectData(props);

  const renderGroup = (params: AutocompleteRenderGroupParams) => {
    if (renderAdditionalOptionGroup && params.group === Categories.AdditionalOptions) {
      return renderAdditionalOptionGroup(params);
    }

    const group: React.ReactNode[] = [];
    if (params.group) {
      group.push(
        <ListSubheader
          disableSticky
          key={params.key}
          component="div"
          sx={{
            color: (theme) => theme.palette.primary.main,
            fontWeight: (theme) => theme.typography.fontWeightMedium,
            padding: (theme) => `${theme.spacing(1)} ${theme.spacing(0.5)}`,
          }}
        >
          <Typography variant="caption" color="primary" fontWeight={600}>
            {params.group === Categories.RecentAccounts && t('hiring-dashboard-common:common.select.recentAccounts')}
            {params.group === Categories.InterviewPanel && t('hiring-dashboard-common:common.select.interviewPanel')}
            {params.group === Categories.AllAccounts && t('hiring-dashboard-common:common.select.allAccounts')}
          </Typography>
        </ListSubheader>
      );
    }
    group.push(params.children);
    return group;
  };

  const getOptionLabel = (option: ZoomUserDataWithCategory) => {
    if (option.id && getAdditionalOptionLabel && isAdditionalOptionId && isAdditionalOptionId(option.id)) {
      return getAdditionalOptionLabel(option);
    }
    return `${option.firstName} ${option.lastName} (${option.email})`;
  };

  const renderOption = (liProps: React.HTMLAttributes<HTMLLIElement>, option: ZoomUserDataWithCategory) => {
    let jsx: React.ReactNode = null;
    if (option.id === EMPTY_ROW_ID) {
      jsx = (
        <Stack direction="row" justifyContent="center">
          <CircularProgress size={20} />
        </Stack>
      );
    }
    if (option.id && isAdditionalOptionId && isAdditionalOptionId(option.id) && renderAdditionalOption) {
      return (
        <ListItem {...liProps} data-testid={option.id}>
          <ListItemButton>{renderAdditionalOption(option)}</ListItemButton>
        </ListItem>
      );
    }

    if (option.id) {
      if (!option.email) {
        return null;
      }
    }

    const isRecentAccount = option.category === Categories.RecentAccounts;
    const label = `${option.firstName} ${option.lastName} (${option.email})`;

    jsx = <MaybeTooltip title={label} label={label} placement="left" />;

    return (
      <ListItem {...liProps}>
        <ListItemButton sx={{ display: 'flex', justifyContent: 'space-between' }}>
          {jsx}
          {isRecentAccount && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveRecentAccount(option.zoomUserId);
              }}
              sx={{ ml: 1, p: 0.25 }}
            >
              <CrossIcon sx={{ fontSize: 16 }} />
            </IconButton>
          )}
        </ListItemButton>
      </ListItem>
    );
  };

  const defaultGetLabel = () => {
    const firstSelectedUser = first(selectedUsers);

    const label = firstSelectedUser
      ? getOptionLabel(firstSelectedUser)
      : t('hiring-dashboard-common:common.select.selectZoomUser');
    if (getLabel) {
      return getLabel(label);
    }

    return (
      <Button
        size="medium"
        fullWidth
        sx={{ borderRadius: '6px', justifyContent: 'space-between', mt: 0.5 }}
        endIcon={<TriangleDownIcon />}
      >
        <Typography noWrap color="text.secondary">
          {label}
        </Typography>
      </Button>
    );
  };

  const getChildSize = (child: React.ReactNode) => {
    if (!child) {
      return 0;
    }

    // These values is by inspecting DOM.
    if (React.isValidElement(child) && child.type === ListSubheader) {
      return 32;
    }

    const children = (child as React.ReactElement)?.props?.children
      ? React.Children.toArray((child as React.ReactElement)?.props.children)
      : undefined;
    if (
      children &&
      children.length === 1 &&
      React.isValidElement(children[0]) &&
      React.isValidElement(children[0].props.children) &&
      isAdditionalOptionId &&
      children[0].props.children.props['data-testid'] &&
      isAdditionalOptionId(children[0].props.children.props['data-testid']) &&
      getAdditionalOptionSize
    ) {
      return getAdditionalOptionSize(child);
    }
    return 32;
  };

  if (isFetchingMore) {
    options.push({
      id: EMPTY_ROW_ID,
    } as ZoomUserDataWithCategory);
  }

  // Adding a check for search query to avoid unmounting of FilterSelect on entering a search query.
  if (isZoomUsersListLoading && isSearchEmpty) {
    return <LinearProgress />;
  }

  if (isZoomUsersListQueryError) {
    return <PublicError error={t('hiring-dashboard-common:common.select.zoomUsersLoadError')} />;
  }

  return (
    <CanDisable disabled={Boolean(disabled)}>
      <FilterSelect
        dataTestId={dataTestId}
        values={selectedValues}
        isOptionEqualToValue={(option, listValue) => option?.zoomUserId === listValue?.zoomUserId}
        filterSelectedOptions
        filterListSelectInterfaceRef={filterListSelectInterfaceRef}
        options={options}
        loading={isZoomUsersListLoading}
        placeholderText={t('hiring-dashboard-common:common.select.selectZoomUser')}
        getOptionLabel={getOptionLabel}
        renderOption={renderOption}
        renderGroup={renderGroup}
        groupBy={(option) => option?.category ?? ''}
        onChange={handleChange}
        getLabel={defaultGetLabel}
        onScrollToEnd={handleScrollToEnd}
        onInputChange={onInputChange}
        getChildSize={getChildSize}
        filterOptions={(options) => options}
      />
    </CanDisable>
  );
};

export default ZoomUserSelect;
